import React, { useState, useRef, useMemo, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { uploadService } from '../../api/uploadService';
import './HtmlDescriptionEditor.css';

// ---------------------------------------------------------------------------
// Custom Quill blot: HTML5 <video> embed.
//
// Quill's built-in `video` format renders an <iframe> (for YouTube/Vimeo),
// so it strips any raw <video> tag pasted via dangerouslyPasteHTML. This
// blot registers a separate format we can insertEmbed() with the file URL,
// producing a native <video controls> element that survives serialization.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined' && !Quill.imports['formats/videofile']) {
  const BlockEmbed = Quill.import('blots/block/embed');

  class VideoFileBlot extends BlockEmbed {
    static create(url) {
      const node = super.create();
      node.setAttribute('src', url);
      node.setAttribute('controls', 'controls');
      node.setAttribute('preload', 'metadata');
      node.setAttribute('playsinline', 'playsinline');
      node.setAttribute('style', 'max-width:100%;border-radius:8px;display:block;margin:1em auto;');
      return node;
    }
    static value(node) {
      return node.getAttribute('src');
    }
  }
  VideoFileBlot.blotName = 'videofile';
  VideoFileBlot.tagName = 'video';

  Quill.register(VideoFileBlot);
}

// ---------------------------------------------------------------------------
// Isolated preview document — mirrors exactly what the ecommerce site renders
// ---------------------------------------------------------------------------
function buildPreviewDoc(html) {
  const content =
    html && html.trim()
      ? html
      : '<p style="color:#9ca3af;text-align:center;padding:48px 0;font-style:italic;">' +
        'Aucune description saisie. Commencez à écrire dans l\'éditeur visuel.' +
        '</p>';

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1.0">
<style>
/* ── Reset ── */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ── Base ── */
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 16px;
  line-height: 1.75;
  color: #374151;
  padding: 28px 32px;
  background: #fff;
}

/* ── Headings ── */
h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  line-height: 1.3;
  color: #111827;
  margin-top: 1.6em;
  margin-bottom: 0.5em;
}
h1:first-child, h2:first-child, h3:first-child,
h4:first-child, h5:first-child, h6:first-child { margin-top: 0; }
h1 { font-size: 2em; }
h2 { font-size: 1.5em; border-bottom: 2px solid #e5e7eb; padding-bottom: 0.3em; }
h3 { font-size: 1.25em; }
h4 { font-size: 1.1em; }
h5 { font-size: 1em; }
h6 { font-size: 0.875em; color: #6b7280; }

/* ── Paragraphs ── */
p { margin: 0 0 1em; }
p:last-child { margin-bottom: 0; }

/* ── Links ── */
a { color: #DC143C; text-decoration: underline; }
a:hover { color: #B8112E; }

/* ── Lists ── */
ul, ol { margin: 0 0 1em; padding-left: 1.75em; }
ul { list-style-type: disc; }
ol { list-style-type: decimal; }
li { margin-bottom: 0.3em; }
li > ul, li > ol { margin-top: 0.3em; margin-bottom: 0; }

/* ── Blockquote ── */
blockquote {
  border-left: 4px solid #DC143C;
  margin: 1.5em 0;
  padding: 0.75em 1.5em;
  color: #6b7280;
  background: #fef9f9;
  font-style: italic;
  border-radius: 0 6px 6px 0;
}

/* ── Code ── */
code {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.875em;
  background: #f3f4f6;
  padding: 0.15em 0.4em;
  border-radius: 3px;
  color: #dc2626;
}
pre {
  background: #1f2937;
  color: #f3f4f6;
  padding: 1em 1.5em;
  border-radius: 8px;
  overflow-x: auto;
  margin: 1.25em 0;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.875em;
  line-height: 1.6;
}
pre code { background: none; color: inherit; padding: 0; font-size: inherit; border-radius: 0; }

/* ── Images ── */
img { max-width: 100%; height: auto; border-radius: 6px; display: block; margin: 0.75em 0; }

/* ── Videos ── */
video { max-width: 100%; height: auto; border-radius: 8px; display: block; margin: 1em 0; background: #000; }
iframe { max-width: 100%; border-radius: 8px; display: block; margin: 1em 0; aspect-ratio: 16/9; width: 100%; }

/* ── Tables ── */
table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5em 0;
  font-size: 0.9em;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
th, td { border: 1px solid #e5e7eb; padding: 0.75em 1em; text-align: left; }
th { background: #f3f4f6; font-weight: 600; color: #374151; }
tr:nth-child(even) td { background: #f9fafb; }

/* ── Inline ── */
strong { font-weight: 700; }
em { font-style: italic; }
s { text-decoration: line-through; }
u { text-decoration: underline; }
sub { font-size: 0.75em; vertical-align: sub; }
sup { font-size: 0.75em; vertical-align: super; }

/* ── HR ── */
hr { border: none; border-top: 2px solid #e5e7eb; margin: 2em 0; }

/* ── Quill alignment classes ── */
.ql-align-center  { text-align: center; }
.ql-align-right   { text-align: right; }
.ql-align-justify { text-align: justify; }

/* ── Quill indent classes ── */
.ql-indent-1 { padding-left:  3em; }
.ql-indent-2 { padding-left:  6em; }
.ql-indent-3 { padding-left:  9em; }
.ql-indent-4 { padding-left: 12em; }
.ql-indent-5 { padding-left: 15em; }
.ql-indent-6 { padding-left: 18em; }
.ql-indent-7 { padding-left: 21em; }
.ql-indent-8 { padding-left: 24em; }

/* ── Quill size classes ── */
.ql-size-small { font-size: 0.75em; }
.ql-size-large { font-size: 1.5em; }
.ql-size-huge  { font-size: 2em;   }

/* ── Quill font classes ── */
.ql-font-serif     { font-family: Georgia, 'Times New Roman', Times, serif; }
.ql-font-monospace { font-family: 'Courier New', Courier, monospace; }
</style>
</head>
<body>${content}</body>
</html>`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function stripHtml(html) {
  return (html || '').replace(/<[^>]*>/g, '');
}

function wordCount(html) {
  const text = stripHtml(html).trim();
  return text ? text.split(/\s+/).filter(Boolean).length : 0;
}

function charCount(html) {
  return stripHtml(html).length;
}

// ---------------------------------------------------------------------------
// Quill toolbar/format config
// ---------------------------------------------------------------------------
const QUILL_FORMATS = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'align',
  'blockquote', 'code-block',
  'link', 'image', 'video', 'videofile',
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function HtmlDescriptionEditor({ value = '', onChange }) {
  const [mode, setMode] = useState('visual');   // 'visual' | 'html' | 'preview'
  const [fullscreen, setFullscreen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const quillRef = useRef(null);

  // ── Image upload handler ──────────────────────────────────────────────────
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image valide.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('La taille de l\'image doit être inférieure à 5 MB.');
        return;
      }

      try {
        setUploading(true);
        const result = await uploadService.uploadMedia(file, 'image');
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, 'image', result.url);
        quill.setSelection(range.index + 1);
      } catch (err) {
        alert('Échec du téléchargement : ' + (err.response?.data?.error || err.message));
      } finally {
        setUploading(false);
      }
    };
  }, []);

  // ── Video upload handler ──────────────────────────────────────────────────
  // Quill's default "video" handler asks for a YouTube URL. We override it to:
  //  - if user picks a file → upload + insert <video controls>
  //  - if user cancels file dialog → fall back to URL prompt (YouTube/Vimeo)
  const videoHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'video/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) {
        // User cancelled file picker — fall back to URL prompt
        const url = window.prompt('URL vidéo (YouTube, Vimeo) :');
        if (!url) return;
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, 'video', url);
        quill.setSelection(range.index + 1);
        return;
      }

      if (!file.type.startsWith('video/')) {
        alert('Veuillez sélectionner un fichier vidéo valide.');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        alert('La taille de la vidéo doit être inférieure à 50 MB.');
        return;
      }

      try {
        setUploading(true);
        const result = await uploadService.uploadMedia(file, 'video');
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const range = quill.getSelection(true) || { index: quill.getLength() };
        // Use the custom 'videofile' blot so Quill renders a real <video> tag
        // (its built-in 'video' format renders an <iframe> and strips raw <video>).
        quill.insertEmbed(range.index, 'videofile', result.url, 'user');
        quill.insertText(range.index + 1, '\n', 'user');
        quill.setSelection(range.index + 2, 0, 'user');
      } catch (err) {
        alert('Échec du téléchargement : ' + (err.response?.data?.error || err.message));
      } finally {
        setUploading(false);
      }
    };
  }, []);

  // ── Quill modules (stable reference) ─────────────────────────────────────
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: { image: imageHandler, video: videoHandler },
    },
    clipboard: { matchVisual: false },
  }), [imageHandler, videoHandler]);

  // ── Derived stats ─────────────────────────────────────────────────────────
  const words = wordCount(value);
  const chars = charCount(value);

  // ── Tab config ────────────────────────────────────────────────────────────
  const tabs = [
    { key: 'visual',   label: 'Éditeur Visuel' },
    { key: 'html',     label: 'Source HTML'    },
    { key: 'preview',  label: 'Aperçu'         },
  ];

  return (
    <div className={`hde${fullscreen ? ' hde--fullscreen' : ''}`}>

      {/* ── Header bar ─────────────────────────────────────────────────── */}
      <div className="hde__header">
        <div className="hde__tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              type="button"
              className={`hde__tab${mode === t.key ? ' hde__tab--active' : ''}`}
              onClick={() => setMode(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="hde__header-right">
          {uploading && (
            <span className="hde__upload-indicator">
              ⏳ Téléchargement de l'image…
            </span>
          )}
          <button
            type="button"
            className="hde__fullscreen-btn"
            onClick={() => setFullscreen(f => !f)}
            title={fullscreen ? 'Quitter le plein écran (Échap)' : 'Plein écran'}
          >
            {fullscreen ? '⊡ Réduire' : '⊞ Plein écran'}
          </button>
        </div>
      </div>

      {/* ── Editor body ────────────────────────────────────────────────── */}
      <div className="hde__body">

        {/* Visual (always mounted so Quill state is preserved) */}
        <div
          className="hde__visual-wrap"
          style={{ display: mode === 'visual' ? 'flex' : 'none' }}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={onChange}
            modules={modules}
            formats={QUILL_FORMATS}
            placeholder="Rédigez une description complète et attrayante pour ce produit…"
            className="hde__quill"
          />
        </div>

        {/* HTML source */}
        {mode === 'html' && (
          <textarea
            className="hde__source"
            value={value}
            onChange={e => onChange(e.target.value)}
            spellCheck={false}
            placeholder="<p>Entrez votre HTML ici…</p>"
          />
        )}

        {/* Isolated preview */}
        {mode === 'preview' && (
          <iframe
            srcDoc={buildPreviewDoc(value)}
            title="Aperçu de la description produit"
            className="hde__preview"
            sandbox="allow-same-origin"
          />
        )}
      </div>

      {/* ── Footer stats bar ───────────────────────────────────────────── */}
      <div className="hde__footer">
        <span className="hde__stat">{words} mot{words !== 1 ? 's' : ''}</span>
        <span className="hde__stat-sep">·</span>
        <span className="hde__stat">{chars} caractère{chars !== 1 ? 's' : ''}</span>
        <span className="hde__mode">
          {mode === 'visual'  ? 'Éditeur Visuel'
           : mode === 'html' ? 'Source HTML'
           :                   'Aperçu isolé'}
        </span>
      </div>
    </div>
  );
}

export default HtmlDescriptionEditor;
